// src/utils/guideMoods.ts
//
// Quick-filter mood chips for the Guide feed. Different from the
// Discover category filter: moods are conversational ("I'm in the
// mood for X"), single-select, and ephemeral (don't persist across
// sessions).
//
// Matching strategy: AI-generated `tags` on Guide cards are loose
// and often abstract ("iconic", "evening", "weekend"). Filtering on
// tags ALONE means a card titled "Best wine bars in Paris" can
// fail to match the "Drinks" mood because its tags are
// ["evening", "iconic"]. To be useful, the matcher checks ALL of:
// title, body, and tags. Title + body is where the topic actually
// lives; tags are a hint, not the source of truth.

export interface MoodDef {
  key: string;
  label: string;
  emoji: string;
  /** Lowercase fragments. Match if any fragment appears as a
   *  substring in the card's normalized title, body, or tags.
   *  Substring (not whole-word) so "café de flore" matches "café"
   *  and "specialty coffee shop" matches "coffee". */
  synonyms: string[];
}

export const GUIDE_MOODS: MoodDef[] = [
  {
    key: 'hungry',
    label: 'Hungry',
    emoji: '🍽️',
    synonyms: [
      'restaurant', 'food', 'dining', 'eatery', 'bistro', 'gastropub',
      'meal', 'lunch', 'dinner', 'brunch', 'breakfast', 'bites',
      'tapas', 'trattoria', 'osteria', 'noodle', 'ramen', 'sushi',
      'pizzeria', 'kitchen', 'canteen', 'diner',
    ],
  },
  {
    key: 'coffee',
    label: 'Coffee',
    emoji: '☕',
    synonyms: [
      'cafe', 'café', 'coffee', 'espresso', 'latte', 'cappuccino',
      'tea', 'patisserie', 'bakery', 'pastry', 'pastries',
      'coffeehouse', 'roaster', 'roastery',
    ],
  },
  {
    key: 'drinks',
    label: 'Drinks',
    emoji: '🍸',
    synonyms: [
      'bar', 'pub', 'cocktail', 'wine', 'beer', 'brewery', 'speakeasy',
      'nightlife', 'rooftop', 'tavern', 'lounge', 'aperitivo',
      'aperitif', 'spirits', 'whisky', 'whiskey', 'tasting',
      'natural wine', 'gin', 'vermouth',
    ],
  },
  {
    key: 'outdoors',
    label: 'Outdoors',
    emoji: '🌳',
    synonyms: [
      'park', 'garden', 'viewpoint', 'trail', 'beach', 'nature',
      'scenic', 'hiking', 'walk', 'walking', 'stroll', 'lake',
      'river', 'waterfront', 'promenade', 'forest', 'botanical',
      'overlook', 'vista', 'outdoor',
    ],
  },
  {
    key: 'culture',
    label: 'Culture',
    emoji: '🎨',
    synonyms: [
      'museum', 'gallery', 'art', 'theater', 'theatre', 'cultural',
      'exhibition', 'exhibit', 'performing-arts', 'concert hall',
      'opera', 'ballet', 'film', 'cinema', 'sculpture', 'painting',
      'curator', 'library', 'archive',
    ],
  },
  {
    key: 'shop',
    label: 'Shop',
    emoji: '🛍️',
    synonyms: [
      'shop', 'shopping', 'market', 'boutique', 'mall', 'bookstore',
      'antiques', 'vintage', 'flea market', 'department store',
      'concept store', 'designer', 'crafts', 'artisan',
    ],
  },
  {
    key: 'history',
    label: 'History',
    emoji: '🏛️',
    synonyms: [
      'historic', 'historical', 'monument', 'heritage', 'ruins',
      'castle', 'palace', 'memorial', 'fortress', 'archaeology',
      'archaeological', 'ancient', 'medieval', 'roman', 'baroque',
      'renaissance', 'colonial',
    ],
  },
];

function normalize(s: string): string {
  return s.toLowerCase().replace(/[_\s]+/g, '-');
}

/** Build a regex for a synonym. Short synonyms (≤4 chars) need
 *  word-boundary matching to avoid false positives like "bar"
 *  matching "barcelona" or "art" matching "smart". Longer synonyms
 *  use plain substring — they're distinctive enough that
 *  collisions are vanishingly rare. */
function synonymRegex(syn: string): RegExp {
  const norm = normalize(syn);
  // Escape regex metacharacters defensively, though our synonyms
  // are alphabetic so this is mostly insurance.
  const escaped = norm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  if (norm.length <= 4) {
    // Treat hyphen and start/end as boundaries since we normalized
    // spaces to hyphens. \b alone wouldn't trigger across hyphens.
    return new RegExp(`(^|-)${escaped}(-|$)`);
  }
  return new RegExp(escaped);
}

/** Card-shape subset we need for matching. Lets the matcher work
 *  on any card-like object (Guide card, postcard, etc.) without
 *  importing the full type. */
interface MatchableCard {
  title?: string;
  body?: string;
  meta?: string;
  tags?: string[];
  list_items?: Array<{ title?: string; blurb?: string }>;
}

/** Does a card match the given mood? Searches title + body + tags
 *  + list-item titles/blurbs for any synonym substring. Always
 *  returns true when mood is null (no filter applied). */
export function cardMatchesMood(
  card: MatchableCard,
  moodKey: string | null
): boolean {
  if (!moodKey) return true;
  const mood = GUIDE_MOODS.find((m) => m.key === moodKey);
  if (!mood) return true;

  // Build the haystack: everything textual on the card, lowercased
  // and concatenated. We don't need to be precious here — substring
  // search is cheap and the haystack stays small (<2KB per card).
  const parts: string[] = [];
  if (card.title) parts.push(card.title);
  if (card.body) parts.push(card.body);
  if (card.meta) parts.push(card.meta);
  if (Array.isArray(card.tags)) parts.push(...card.tags);
  if (Array.isArray(card.list_items)) {
    for (const item of card.list_items) {
      if (item?.title) parts.push(item.title);
      if (item?.blurb) parts.push(item.blurb);
    }
  }
  const haystack = normalize(parts.join(' '));
  if (!haystack) return false;

  for (const syn of mood.synonyms) {
    if (synonymRegex(syn).test(haystack)) return true;
  }
  return false;
}
